import { DynamicQuestionnaireFields } from '@core/data-access/models/api/dynamic-questionnaire-fields.model';
import {
  CreateFormFieldSettings,
  OptionalCreateFormFieldSettings
} from '@core/data-access/models/dynamic-form-field/create-form-field-settings.model';
import {
  BaseDynamicFormField,
  DropdownField,
  DropdownOption,
  DynamicFormField,
  FieldType,
  QuestionnaireDynamicFormFields,
  TextInputField
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  QuestionnaireValueIds,
  QuestionnaireVisibilityField,
  QuestionnaireVisibilityFieldOptions,
  QuestionnaireVisibilityFields
} from '@core/data-access/models/dynamic-form-field/questionnaire-field-visibility.model';
import { FormField, FormFields, FormFieldValue } from '@core/data-access/models/form/form-store.model';
import { FormValue, FormValues } from '@core/data-access/models/form/form.model';
import { PersonType } from '@core/data-access/models/person.model';
import { TicketSendingOption } from '@core/data-access/models/shared/ticket-sending-options.model';
import {
  removeNonQuestionnaireFormFields,
  setQuestionnaireFormFieldKey
} from '@core/utils/form-fields/form-field-helpers';
import { isCheckboxFormField, isCheckboxFormFieldValue } from '@core/utils/form-fields/form-field-type-guard';
import { SendingOptionModel } from '@products/models/product-selection.model';
import { TicketPersonId } from '@products/models/tariff.model';
import { SelectOption } from '@store/exhibition/exhibition.interface';
import {
  concat,
  filter,
  find,
  forEach,
  has,
  includes,
  isEmpty,
  isNil,
  keyBy,
  keys,
  map,
  merge,
  pickBy,
  reduce,
  some
} from 'lodash';
import {
  isQuestionnaireCheckboxField,
  isQuestionnaireDropdownField,
  isQuestionnaireRadioField
} from './dynamic-form-field-type-guards';

export const defaultCreateFormFieldSettings: CreateFormFieldSettings = {
  isRequired: false,
  isHidden: false,
  isDisabled: false,
  isQuestionnaireField: false,
  columns: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }
};

export const createTextInputOrDropdownField = (
  baseDynamicFormField: BaseDynamicFormField,
  selectOptions: SelectOption[],
  optionalCreateFormFieldSettings: OptionalCreateFormFieldSettings
): TextInputField | DropdownField => {
  if (isEmpty(selectOptions)) {
    return { ...baseDynamicFormField, ...optionalCreateFormFieldSettings, type: FieldType.TextInput } as TextInputField;
  }

  return {
    ...baseDynamicFormField,
    type: FieldType.Dropdown,
    options: mapSelectOptionsToDropdownOptions(selectOptions)
  } as DropdownField;
};

export const isFieldHidden = (dynamicFormField: DynamicFormField, formFields: FormFields) =>
  has(formFields[dynamicFormField.key], 'isHidden')
    ? formFields[dynamicFormField.key].isHidden
    : dynamicFormField.isHidden;

export const setChangedQuestionnaireFieldsVisibility = (
  dynamicFormFields: QuestionnaireDynamicFormFields,
  currentFormValues: FormValues
) => {
  const questionnaireVisibilityFields = mapDynamicFormFieldsToVisibilityFields(dynamicFormFields);
  const questionnaireValueIds = setQuestionnaireValueIdsFromFormValues(
    questionnaireVisibilityFields,
    currentFormValues
  );
  const questionnaireFieldsVisibility = setQuestionnaireFieldsVisibility(
    questionnaireVisibilityFields,
    questionnaireValueIds
  );

  const dynamicFormFieldsByKey = keyBy(dynamicFormFields, 'key');

  // Only the fields whose visibility changed are forwarded, so that a form field change keeps carrying its changes only.
  return pickBy(
    questionnaireFieldsVisibility,
    ({ isHidden }, fieldKey) => isHidden !== dynamicFormFieldsByKey[fieldKey]?.isHidden
  ) as FormFields;
};

export const setSetSyncedQuestionnairesFormFieldsVisibility = (
  dynamicQuestionnairesFields: Record<TicketPersonId, DynamicQuestionnaireFields>,
  formFields: FormFields
) => {
  const updatedQuestionnaireFormFields: FormFields = reduce(
    dynamicQuestionnairesFields,
    (updatedQuestionnaireFormFields, dynamicQuestionnaireFields) =>
      merge(
        {},
        updatedQuestionnaireFormFields,
        setSyncedQuestionnaireFormFieldsVisibility(dynamicQuestionnaireFields, formFields)
      ),
    {}
  );

  return merge({}, formFields, updatedQuestionnaireFormFields);
};

export const setSyncedQuestionnaireFormFieldsVisibility = (
  dynamicQuestionnaireFields: DynamicQuestionnaireFields,
  formFields: FormFields
) => {
  const questionnaireVisibilityFields = mapDynamicQuestionnaireFieldsToVisibilityFields(dynamicQuestionnaireFields);
  const questionnaireValueIds = setQuestionnaireValueIdsFromFormFields(
    questionnaireVisibilityFields,
    removeNonQuestionnaireFormFields(formFields)
  );

  return merge({}, formFields, setQuestionnaireFieldsVisibility(questionnaireVisibilityFields, questionnaireValueIds));
};

export const isFieldDisabled = (dynamicFormField: DynamicFormField, formFields: FormFields) => {
  const { key, isHidden, isDisabled } = dynamicFormField;

  const formField = formFields[key];
  const formFieldHasProperty = has(formField, 'isHidden') || has(formField, 'isDisabled');
  const isFormFieldHiddenOrDisabled = formField?.isHidden || formField?.isDisabled;
  const isDynamicFormFieldHiddenOrDisabled = isHidden || isDisabled;

  return formFieldHasProperty ? isFormFieldHiddenOrDisabled : isDynamicFormFieldHiddenOrDisabled;
};

export const setSendToOwnerFieldVisibility = (
  personType: PersonType,
  ticketSendingOptions: SendingOptionModel[],
  selectedTicketSendingOption: TicketSendingOption
) => {
  const setSendToOwnerFieldCheckedAndVisible = (isSendToOwnerChecked: boolean, isSendToOwnerHidden: boolean) => ({
    isSendToOwnerChecked,
    isSendToOwnerHidden
  });

  if (personType === PersonType.BuyerIsHolder) {
    return setSendToOwnerFieldCheckedAndVisible(false, true);
  }

  if (selectedTicketSendingOption === TicketSendingOption.AllToBuyer) {
    return setSendToOwnerFieldCheckedAndVisible(false, true);
  }

  if (selectedTicketSendingOption === TicketSendingOption.NormalPerOwner) {
    return setSendToOwnerFieldCheckedAndVisible(true, true);
  }

  const enabledTicketSendingOptions = filter(ticketSendingOptions, {
    isEnabled: true
  });

  const isSingleTicketSendingOptionEnabled = enabledTicketSendingOptions.length === 1;

  const isMobileSendingOptionEnabledAndSelected = some(enabledTicketSendingOptions, {
    isEnabled: true,
    isSelected: true,
    value: TicketSendingOption.MobilePerBuyer || TicketSendingOption.MobilePerOwner
  });

  const isSelectedTicketSendingOptionMobilePerOwner =
    selectedTicketSendingOption === TicketSendingOption.MobilePerOwner;

  if (isSingleTicketSendingOptionEnabled && isMobileSendingOptionEnabledAndSelected) {
    return setSendToOwnerFieldCheckedAndVisible(isSelectedTicketSendingOptionMobilePerOwner, true);
  }

  if (isSelectedTicketSendingOptionMobilePerOwner) {
    const isMobilePerBuyerSendingOptionDisabled = some(ticketSendingOptions, {
      isEnabled: false,
      value: TicketSendingOption.MobilePerBuyer
    });

    if (isMobilePerBuyerSendingOptionDisabled) {
      return setSendToOwnerFieldCheckedAndVisible(true, true);
    }

    const isMobilePerOwnerSendingOptionDisabled = some(ticketSendingOptions, {
      isEnabled: false,
      value: TicketSendingOption.MobilePerOwner
    });

    if (isMobilePerOwnerSendingOptionDisabled) {
      return setSendToOwnerFieldCheckedAndVisible(false, true);
    }

    return setSendToOwnerFieldCheckedAndVisible(false, false);
  }

  const isTicketRetrievalLinkEnabledAndSelected = some(ticketSendingOptions, {
    isEnabled: true,
    isSelected: true,
    value: TicketSendingOption.TicketRetrievalLinkBuyer || TicketSendingOption.TicketRetrievalLink
  });

  const isSelectedSendingOptionTicketRetrievalLink =
    selectedTicketSendingOption === TicketSendingOption.TicketRetrievalLink;

  if (isSingleTicketSendingOptionEnabled && isTicketRetrievalLinkEnabledAndSelected) {
    return setSendToOwnerFieldCheckedAndVisible(isSelectedSendingOptionTicketRetrievalLink, true);
  }

  if (isSelectedSendingOptionTicketRetrievalLink) {
    const isTicketRetrievalLinkToBuyerSendingOptionDisabled = some(ticketSendingOptions, {
      isEnabled: false,
      value: TicketSendingOption.TicketRetrievalLinkBuyer
    });

    if (isTicketRetrievalLinkToBuyerSendingOptionDisabled) {
      return setSendToOwnerFieldCheckedAndVisible(true, true);
    }

    const isTicketRetrievalLinkToVisitorSendingOptionDisabled = some(ticketSendingOptions, {
      isEnabled: false,
      value: TicketSendingOption.TicketRetrievalLink
    });

    if (isTicketRetrievalLinkToVisitorSendingOptionDisabled) {
      return setSendToOwnerFieldCheckedAndVisible(false, true);
    }

    return setSendToOwnerFieldCheckedAndVisible(false, false);
  }

  return setSendToOwnerFieldCheckedAndVisible(false, true);
};

const mapSelectOptionsToDropdownOptions = (selectOptions: SelectOption[]): DropdownOption[] =>
  selectOptions.map((option) => ({ key: option.key, value: option.value, isDisabled: false }));

/**
 * A questionnaire field is revealed by the option (value id) its `previousValueId` points to. Visibility is derived
 * from the currently selected value ids of the whole questionnaire, so that it does not depend on which field changed
 * and cannot drift out of sync when several fields depend on the same option.
 *
 * A field is visible when its `previousValueId` is selected and the field owning that option is itself visible, which
 * cascades hiding down a chain of dependent fields (checkbox -> checkbox -> ...) once a field higher up is hidden.
 */
const setQuestionnaireFieldsVisibility = (
  questionnaireVisibilityFields: QuestionnaireVisibilityFields,
  { selectedValueIds, resolvableValueIds }: QuestionnaireValueIds
) => {
  const valueIdOwnerFields = reduce(
    questionnaireVisibilityFields,
    (valueIdOwnerFields, questionnaireVisibilityField) => {
      forEach(questionnaireVisibilityField.options, ({ valueId }) => {
        valueIdOwnerFields[valueId] = questionnaireVisibilityField;
      });

      return valueIdOwnerFields;
    },
    {} as Record<number, QuestionnaireVisibilityField>
  );

  const fieldsVisibility: Record<string, boolean> = {};

  const isQuestionnaireFieldVisible = (
    { key, previousValueId }: QuestionnaireVisibilityField,
    visitedFieldKeys: string[]
  ) => {
    if (isNil(previousValueId) || !includes(resolvableValueIds, previousValueId)) {
      return true;
    }

    if (has(fieldsVisibility, key)) {
      return fieldsVisibility[key];
    }

    if (includes(visitedFieldKeys, key)) {
      return false;
    }

    const previousValueField = valueIdOwnerFields[previousValueId];

    const isVisible =
      includes(selectedValueIds, previousValueId) &&
      (isNil(previousValueField) || isQuestionnaireFieldVisible(previousValueField, [...visitedFieldKeys, key]));

    fieldsVisibility[key] = isVisible;

    return isVisible;
  };

  return reduce(
    questionnaireVisibilityFields,
    (updatedQuestionnaireFormFields: FormFields, questionnaireVisibilityField) => {
      const { key, previousValueId } = questionnaireVisibilityField;

      if (isNil(previousValueId) || !includes(resolvableValueIds, previousValueId)) {
        return updatedQuestionnaireFormFields;
      }

      updatedQuestionnaireFormFields[key] = {
        isHidden: !isQuestionnaireFieldVisible(questionnaireVisibilityField, []),
        isQuestionnaire: true
      } as FormField;

      return updatedQuestionnaireFormFields;
    },
    {}
  );
};

const mapDynamicFormFieldsToVisibilityFields = (
  dynamicFormFields: QuestionnaireDynamicFormFields
): QuestionnaireVisibilityFields =>
  map(dynamicFormFields, (dynamicFormField) => {
    const { key, previousValueId } = dynamicFormField;

    const isQuestionnaireFieldWithOptions =
      isQuestionnaireDropdownField(dynamicFormField) ||
      isQuestionnaireCheckboxField(dynamicFormField) ||
      isQuestionnaireRadioField(dynamicFormField);

    return {
      key,
      previousValueId,
      options: isQuestionnaireFieldWithOptions
        ? map(dynamicFormField.options, ({ key, value }) => ({ valueId: +key, value }))
        : []
    };
  });

const mapDynamicQuestionnaireFieldsToVisibilityFields = (
  dynamicQuestionnaireFields: DynamicQuestionnaireFields
): QuestionnaireVisibilityFields =>
  map(dynamicQuestionnaireFields, (dynamicQuestionnaireField) => {
    const { id, type, values, previousValueId } = dynamicQuestionnaireField;

    const isQuestionnaireFieldWithOptions = includes(
      [FieldType.Dropdown, FieldType.Checkbox, FieldType.Radio],
      type as FieldType
    );

    return {
      key: isEmpty(values) ? id.toString() : setQuestionnaireFormFieldKey(dynamicQuestionnaireField),
      previousValueId,
      options: isQuestionnaireFieldWithOptions ? map(values, ({ id, text }) => ({ valueId: id, value: text })) : []
    };
  });

const setQuestionnaireValueIdsFromFormValues = (
  questionnaireVisibilityFields: QuestionnaireVisibilityFields,
  formValues: FormValues
) =>
  setQuestionnaireValueIds(questionnaireVisibilityFields, ({ key, options }) => {
    const formValue = formValues[key];

    if (isNil(formValue)) {
      return null;
    }

    return isCheckboxFormFieldValue(formValue)
      ? setCheckedValueIds(formValue as object, (checkboxValue) => checkboxValue === true)
      : setSelectedOptionValueIds(options, formValue);
  });

const setQuestionnaireValueIdsFromFormFields = (
  questionnaireVisibilityFields: QuestionnaireVisibilityFields,
  formFields: FormFields
) =>
  setQuestionnaireValueIds(questionnaireVisibilityFields, ({ key, options }) => {
    const formField = formFields[key];

    if (isNil(formField?.value)) {
      return null;
    }

    return isCheckboxFormField(formField)
      ? setCheckedValueIds(formField.value, (checkboxFormField: FormField) => checkboxFormField?.value === true)
      : setSelectedOptionValueIds(options, formField.value);
  });

/**
 * A field's options are resolvable only when the field itself is part of the given values - form fields are synced as a
 * partial set of changed fields, and a field whose answer is not part of that set must keep the visibility it already
 * has instead of being hidden as unanswered.
 */
const setQuestionnaireValueIds = (
  questionnaireVisibilityFields: QuestionnaireVisibilityFields,
  setFieldSelectedValueIds: (questionnaireVisibilityField: QuestionnaireVisibilityField) => number[] | null
) =>
  reduce(
    questionnaireVisibilityFields,
    (questionnaireValueIds: QuestionnaireValueIds, questionnaireVisibilityField) => {
      const { options } = questionnaireVisibilityField;

      if (isEmpty(options)) {
        return questionnaireValueIds;
      }

      const fieldSelectedValueIds = setFieldSelectedValueIds(questionnaireVisibilityField);

      if (isNil(fieldSelectedValueIds)) {
        return questionnaireValueIds;
      }

      return {
        selectedValueIds: concat(questionnaireValueIds.selectedValueIds, fieldSelectedValueIds),
        resolvableValueIds: concat(
          questionnaireValueIds.resolvableValueIds,
          map(options, ({ valueId }) => valueId)
        )
      };
    },
    { selectedValueIds: [], resolvableValueIds: [] }
  );

const setCheckedValueIds = (checkboxValues: object, isCheckboxChecked: (checkboxValue: unknown) => boolean) =>
  map(keys(pickBy(checkboxValues, isCheckboxChecked)), (checkboxValueId) => +checkboxValueId);

const setSelectedOptionValueIds = (
  options: QuestionnaireVisibilityFieldOptions,
  selectedValue: FormValue | FormFieldValue
) => {
  const selectedOption = find(options, ({ value }) => value === selectedValue);

  return isNil(selectedOption) ? [] : [selectedOption.valueId];
};
